import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import SchedulingConfigSection from "./SchedulingConfigSection";
import SchedulingPoliciesSection from "./SchedulingPoliciesSection";

export default function SchedulingRulesStep({
  config,
  configDirty,
  configSaved,
  saving,
  scheduling,
  windows,
  participants,
  tournaments,
  fields,
  handleConfigChange,
  handleNullableConfigChange,
  handleToggleChange,
  handleDailyRulesChange,
  saveConfig,
  discardConfigChanges,
  intl,
}) {
  const status = configDirty ? "pending" : configSaved ? "saved" : "unsaved";
  const policyOverrideCount = Number(Boolean(config.ignore_operating_hours)) + Number(Boolean(config.ignore_bookings));

  return (
    <Box>
      {configDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: "Cambios sin guardar en la configuración" })}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 320px" },
          gap: 2,
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <SchedulingConfigSection
            windows={windows}
            handleDailyRulesChange={handleDailyRulesChange}
            config={config}
            handleConfigChange={handleConfigChange}
            handleNullableConfigChange={handleNullableConfigChange}
            intl={intl}
          />

          <SchedulingPoliciesSection
            config={config}
            participants={participants}
            tournaments={tournaments}
            fields={fields}
            onChange={handleDailyRulesChange}
            disabled={saving || scheduling}
            intl={intl}
          />

          <Accordion
            disableGutters
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "4px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
                sx={{ minWidth: 0 }}
              >
                <SettingsIcon color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1">
                    {intl.formatMessage({ id: "scheduling.rules.availabilityPoliciesTitle" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "scheduling.rules.availabilityPoliciesHelp" })}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={intl.formatMessage(
                    { id: "scheduling.rules.availabilityPoliciesSummary" },
                    { policies: policyOverrideCount },
                  )}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="subtitle1">
                  {intl.formatMessage({ id: "scheduling.rules.bookingPolicies" })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {intl.formatMessage({ id: "scheduling.rules.bookingPoliciesHelp" })}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(config.ignore_operating_hours)}
                        onChange={handleToggleChange("ignore_operating_hours")}
                        size="small"
                      />
                    }
                    label={intl.formatMessage({ id: "Ignorar horarios de apertura" })}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(config.ignore_bookings)}
                        onChange={handleToggleChange("ignore_bookings")}
                        size="small"
                      />
                    }
                    label={intl.formatMessage({ id: "Ignorar reservas existentes" })}
                  />
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Stack>

        <RulesSummary
          config={config}
          configDirty={configDirty}
          configSaved={configSaved}
          discardConfigChanges={discardConfigChanges}
          intl={intl}
          saveConfig={saveConfig}
          saving={saving}
          status={status}
        />
      </Box>
    </Box>
  );
}

function RulesSummary({ config, configDirty, configSaved, discardConfigChanges, intl, saveConfig, saving, status }) {
  const values = [
    {
      label: intl.formatMessage({ id: "scheduling.rules.weekdayOverrides" }),
      value: (config.weekday_match_limits || []).length,
    },
    {
      label: intl.formatMessage({ id: "scheduling.rules.dateExceptions" }),
      value: (config.date_match_limits || []).length,
    },
    {
      label: intl.formatMessage({ id: "scheduling.rules.unavailableTimes" }),
      value: (config.player_unavailability || []).length,
    },
    {
      label: intl.formatMessage({ id: "scheduling.rules.resourceRules" }),
      value: (config.resource_preferences || []).length,
    },
    {
      label: intl.formatMessage({ id: "scheduling.rules.transferRules" }),
      value: (config.club_transfers || []).length,
    },
  ];

  return (
    <Card variant="outlined" sx={{ alignSelf: "start", position: { xl: "sticky" }, top: { xl: 16 } }}>
      <CardContent>
        <Typography variant="overline" color="primary.main">
          {intl.formatMessage({ id: "scheduling.rules.configurationStatus" })}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{intl.formatMessage({ id: "scheduling.rules.summaryTitle" })}</Typography>
          <ConfigStatusChip status={status} intl={intl} />
        </Stack>
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          {values.map((item) => (
            <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <PolicyState
            label={intl.formatMessage({ id: "scheduling.rules.openingHours" })}
            ignored={Boolean(config.ignore_operating_hours)}
            intl={intl}
          />
          <PolicyState
            label={intl.formatMessage({ id: "scheduling.rules.existingBookings" })}
            ignored={Boolean(config.ignore_bookings)}
            intl={intl}
          />
        </Stack>
        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
          {intl.formatMessage({ id: "scheduling.rules.summaryHelp" })}
        </Alert>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={saveConfig}
            disabled={saving || (!configDirty && configSaved)}
          >
            {intl.formatMessage({ id: "Guardar configuración" })}
          </Button>
          {configDirty && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={discardConfigChanges}
              disabled={saving}
            >
              {intl.formatMessage({ id: "Descartar" })}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function PolicyState({ label, ignored, intl }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        color={ignored ? "warning" : "default"}
        label={intl.formatMessage({ id: ignored ? "scheduling.rules.ignored" : "scheduling.rules.respected" })}
      />
    </Stack>
  );
}

function ConfigStatusChip({ status, intl }) {
  const labels = {
    saved: "scheduling.rules.saved",
    pending: "scheduling.rules.pending",
    unsaved: "scheduling.rules.unsaved",
  };
  const colors = { saved: "success", pending: "warning", unsaved: "warning" };

  return (
    <Chip size="small" variant="outlined" color={colors[status]} label={intl.formatMessage({ id: labels[status] })} />
  );
}
